import type { RefOption } from '../../api';
import { useInvalidUntilFocus } from './useInvalidUntilFocus';

/**
 * SelectControl — a `<select>` wearing the Design's field box.
 *
 * WHY IT EXISTS. Three of the wizard's details are foreign keys into `refData`
 * (`categoryId`, `accessId`, `departmentId`). The Design draws Primary Category as a
 * plain text box and gives the other two no field at all, so there is no drawn
 * control to copy for a bounded choice — a text input cannot represent an id.
 *
 * WHAT IS DESIGN AND WHAT IS NOT. Every visual property here is lifted from
 * `FormField`'s own single-line input, unchanged and in the same order:
 *
 *     h-field w-full min-w-0 rounded-md border border-border-subtle bg-transparent
 *     px-field-x text-xs font-light text-ink
 *     outline-none focus-visible:border-ring-focus
 *
 * So the box is the measured 31px field with the same r7.5 border, the same 0.2
 * stroke and the same focus ring. Nothing is invented: this is the drawn field with
 * a `<select>` inside it instead of an `<input>`. It is passed through FormField's
 * `children` slot, which the port added for exactly this case, so the label, the
 * `gap-field-gap` label-to-field step and the grid placement all stay FormField's.
 *
 * The one addition a select needs and an input does not is the empty option, so an
 * unset FK reads as a prompt rather than silently defaulting to whatever happens to
 * be first in `refData`. Its text is the placeholder convention, not frame copy.
 */
export interface SelectControlProps {
  /** Matches the label's htmlFor, as FormField's own input does. */
  name: string;
  /** The current id. 0 (or any unmatched id) shows the empty option. */
  value: number;
  onChange: (id: number) => void;
  options: RefOption[];
  required?: boolean;
  invalid?: boolean;
  describedBy?: string;
  /** Registers the node with the Vm's field map (focus-on-error). */
  registerRef?: (el: HTMLSelectElement | null) => void;
}

export function SelectControl({
  name,
  value,
  onChange,
  options,
  required = false,
  invalid,
  describedBy,
  registerRef,
}: SelectControlProps) {
  // Same "red until moused back into" behaviour as FormField's inputs — the
  // wrapping FormField reddens the LABEL, this reddens the box; both clear on the
  // same mouse press. See useInvalidUntilFocus.
  const { showInvalid, onPointerDown } = useInvalidUntilFocus(invalid);
  return (
    <select
      id={name}
      name={name}
      required={required}
      ref={registerRef}
      value={options.some((o) => o.id === value) ? String(value) : ''}
      onChange={(e) => onChange(Number(e.target.value))}
      {...(invalid ? { 'aria-invalid': true } : {})}
      {...(describedBy ? { 'aria-describedby': describedBy } : {})}
      onPointerDown={onPointerDown}
      // Verbatim from FormField's single-line input — see the header.
      className={[
        'h-field w-full min-w-0 rounded-md border bg-transparent',
        // Red while invalid, until the user mouses back in; the faint line
        // otherwise. Focus still deepens to the ring colour.
        showInvalid ? 'border-error' : 'border-border-subtle',
        'px-field-x text-xs font-light text-ink',
        'outline-none focus-visible:border-ring-focus',
      ].join(' ')}
    >
      <option value="">Select…</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
